/**
 * Servis event confirmation — "did it happen?".
 *
 *   <PotvrzeniBlock />       status strip in the event detail modal (state,
 *                            who/when, the confirm / change / clear actions)
 *   <ServisPotvrzeniModal /> the confirm dialog: Proběhlo / Neproběhlo, and —
 *                            for users who may edit — an option to re-plan a
 *                            no-show right away
 *
 * Anyone with servis:read may confirm (only once the event has started — the
 * server enforces it); clearing a confirmation and re-planning need
 * servis:write. Backed by POST/DELETE `/v1/servis/events/{id}/confirm`.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { DialogCrown, Modal } from '@/components/Modal';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate, formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { TextInput } from '@/components/TextInput';
import {
  useConfirmServisEventMutation,
  useServisEventQuery,
  useServisTerminyVozuQuery,
  useUnconfirmServisEventMutation,
  type ServisEventDetail,
  type ServisPotvrzeniStav,
} from './api';
import { eventSubtitle, eventTitle, formatDatumCas, formatPotvrzeno, parseMesice, terminyDruhu } from './helpers';

const STAV_OPTIONS: { value: ServisPotvrzeniStav; label: string }[] = [
  { value: 'probehlo', label: 'Proběhlo' },
  { value: 'neprobehlo', label: 'Neproběhlo' },
];

function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return 'Událost ještě nezačala.';
  return err instanceof ApiError ? err.message : 'Operace se nezdařila';
}

// ============================================================================
// Dialog
// ============================================================================

interface ServisPotvrzeniModalProps {
  /** Event to confirm; null = closed. */
  eventId: number | null;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after a "Neproběhlo" the user chose to re-plan. The host opens the
   * event for editing (or, when it already is the edit modal, points the user
   * at the date fields).
   */
  onReschedule?: (id: number) => void;
}

export function ServisPotvrzeniModal({ eventId, onOpenChange, onReschedule }: ServisPotvrzeniModalProps) {
  if (eventId == null) return null;
  return (
    <Modal open onOpenChange={onOpenChange} maxWidth={520}>
      <PotvrzeniDialogBody eventId={eventId} onClose={() => onOpenChange(false)} onReschedule={onReschedule} />
    </Modal>
  );
}

function PotvrzeniDialogBody({
  eventId,
  onClose,
  onReschedule,
}: {
  eventId: number;
  onClose: () => void;
  onReschedule?: (id: number) => void;
}) {
  const { can } = useModuleAccess();
  const canWrite = can('servis', 'write');
  const query = useServisEventQuery(eventId);
  const detail = query.data?.event;
  const mutation = useConfirmServisEventMutation();

  // Untouched → show the current state (re-opened via "Změnit"), else Proběhlo.
  const [picked, setStav] = useState<ServisPotvrzeniStav | null>(null);
  const stav: ServisPotvrzeniStav = picked ?? detail?.potvrzeni_stav ?? 'probehlo';
  const [preplanovat, setPreplanovat] = useState(false);

  const reschedule = stav === 'neprobehlo' && canWrite && preplanovat;

  // Done → the car's STK / service date follows: úkon day + N months. The
  // intervals differ per car, so they are typed here (12 / 24 prefilled).
  const kinds = terminyDruhu(detail?.druh_ukonu);
  const [servisM, setServisM] = useState('12');
  const [stkM, setStkM] = useState('24');
  const dServis = useDebouncedValue(servisM, 250);
  const dStk = useDebouncedValue(stkM, 250);
  const showTermin = stav === 'probehlo' && kinds.length > 0;
  const preview = useServisTerminyVozuQuery(
    eventId,
    { servis: parseMesice(dServis) ?? undefined, stk: parseMesice(dStk) ?? undefined },
    showTermin && !!detail,
  );
  const car = showTermin ? (preview.data?.vozidlo ?? null) : null;
  const mesiceOf = (k: 'servis' | 'stk') => parseMesice(k === 'servis' ? servisM : stkM);
  const mesiceInvalid = !!car && kinds.some((k) => mesiceOf(k) === null);

  async function submit() {
    let res;
    try {
      res = await mutation.mutateAsync({
        id: eventId,
        stav,
        ...(car
          ? {
              servis_mesice: kinds.includes('servis') ? (mesiceOf('servis') ?? undefined) : undefined,
              stk_mesice: kinds.includes('stk') ? (mesiceOf('stk') ?? undefined) : undefined,
            }
          : {}),
      });
    } catch (err) {
      toast.error(errorMessage(err));
      return;
    }
    const written = (res.terminy ?? []).filter((t) => t.akce === 'zapsat');
    toast.ok(
      stav !== 'probehlo'
        ? 'Označeno jako neproběhlo'
        : written.length > 0
          ? `Provedení potvrzeno · ${written
              .map((t) => `${t.druh === 'stk' ? 'STK' : 'servis'} vozu ${formatDate(t.nove)}`)
              .join(', ')}`
          : 'Provedení potvrzeno',
    );
    if (stav === 'probehlo' && res.servis_km?.akce === 'zapsat' && res.servis_km.dalsi_km != null) {
      toast.ok(`Půjčovna: další servisní prohlídka při ${formatInt(res.servis_km.dalsi_km)} km`);
    }
    onClose();
    if (reschedule) onReschedule?.(eventId);
  }

  return (
    <>
      <DialogCrown
        tone="neutral"
        eyebrow="Servis · Potvrzení"
        eyebrowMeta={detail ? `№ ${detail.id_udalosti}` : undefined}
        title={
          <>
            Potvrdit <em>provedení</em>
          </>
        }
        sub={
          detail ? (
            <>
              <b className="font-semibold text-ink">{eventTitle(detail)}</b>
              {eventSubtitle(detail) ? <> · {eventSubtitle(detail)}</> : null}
              <br />
              <span className="font-mono text-[12px]">
                {formatDatumCas(detail.datum_od)} – {formatDatumCas(detail.datum_do).split(' ').pop()}
              </span>
            </>
          ) : (
            'Načítám…'
          )
        }
      />
      <Modal.Body className="px-8 py-6">
        {query.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !detail ? (
          <p className="text-[12px] text-ink-3">Událost nebyla nalezena. Možná byla mezitím smazána.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                Proběhla událost?
              </div>
              <SegmentedControl<ServisPotvrzeniStav>
                aria-label="Provedení události"
                options={STAV_OPTIONS}
                value={stav}
                onChange={setStav}
              />
            </div>
            {showTermin && (
              <TerminyVozuBlock
                loading={preview.isLoading}
                data={preview.data}
                kinds={kinds}
                servisM={servisM}
                stkM={stkM}
                onServis={setServisM}
                onStk={setStkM}
              />
            )}
            {stav === 'neprobehlo' &&
              (canWrite ? (
                <Checkbox
                  checked={preplanovat}
                  onChange={(e) => setPreplanovat(e.target.checked)}
                  label="Rovnou přeplánovat — nastavit nový termín"
                  data-testid="potvrzeni-preplanovat"
                />
              ) : (
                <p className="text-[12px] text-ink-3">
                  Nový termín může nastavit kolega s právem úprav.
                </p>
              ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        meta={
          <>
            Esc · <b>zavřít</b>
          </>
        }
        actions={
          <>
            <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={mutation.isPending}>
              Zrušit
            </Button>
            <Button
              type="button"
              variant="ink"
              size="sm"
              onClick={() => void submit()}
              disabled={!detail || mutation.isPending || mesiceInvalid}
              data-testid="potvrzeni-submit"
            >
              {reschedule ? 'Potvrdit a přeplánovat' : 'Potvrdit'}
            </Button>
          </>
        }
      />
    </>
  );
}

// ============================================================================
// Car deadlines — the intervals and what they would do
// ============================================================================

const NENALEZENO: Record<string, string> = {
  bez_vozidla: 'Úkon nemá SPZ ani VIN — termín vozu se neaktualizuje.',
  nenalezeno: 'Vozidlo se v evidenci nenašlo — termín se neaktualizuje.',
  nejednoznacne: 'SPZ odpovídá více vozům — termín se neaktualizuje.',
};

function TerminyVozuBlock({
  loading,
  data,
  kinds,
  servisM,
  stkM,
  onServis,
  onStk,
}: {
  loading: boolean;
  data: ReturnType<typeof useServisTerminyVozuQuery>['data'];
  kinds: Array<'servis' | 'stk'>;
  servisM: string;
  stkM: string;
  onServis: (v: string) => void;
  onStk: (v: string) => void;
}) {
  if (loading && !data) return <Skeleton className="h-16 w-full" />;
  if (!data) return null;
  const car = data.vozidlo;

  if (!car) {
    return (
      <p className="text-[12px] text-ink-3" data-testid="terminy-vozu-nenalezeno">
        {NENALEZENO[data.duvod ?? 'nenalezeno']}
      </p>
    );
  }

  const field = (k: 'servis' | 'stk') => {
    const value = k === 'servis' ? servisM : stkM;
    const plan = data.terminy.find((t) => t.druh === k);
    const invalid = parseMesice(value) === null;
    let hint = '';
    if (invalid) hint = 'Zadejte celé číslo 1–120.';
    else if (plan?.akce === 'zapsat')
      hint = `Nový termín ${formatDate(plan.nove)}${plan.aktualni ? ` (dnes ${formatDate(plan.aktualni)})` : ''}`;
    else if (plan?.akce === 'ponechat_pozdejsi')
      hint = `Vůz už má pozdější termín (${formatDate(plan.aktualni)}) — beze změny.`;
    else if (plan?.akce === 'v_minulosti')
      hint = `Nový termín (${formatDate(plan.nove)}) už uplynul — beze změny.`;
    return (
      <Field
        key={k}
        label={k === 'servis' ? 'Další servis za' : 'Další STK za'}
        state={invalid ? 'error' : 'default'}
        hint={hint}
        hintTone={invalid ? 'error' : plan?.akce === 'zapsat' ? 'ok' : 'neutral'}
      >
        <TextInput
          type="number"
          min={1}
          max={120}
          step={1}
          suffix="měsíců"
          value={value}
          onChange={(e) => (k === 'servis' ? onServis : onStk)(e.currentTarget.value)}
          data-testid={k === 'servis' ? 'potvrzeni-servis-mesice' : 'potvrzeni-stk-mesice'}
        />
      </Field>
    );
  };

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-4" data-testid="terminy-vozu">
      <div>
        <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Termíny vozu</div>
        <div className="mt-1 text-[12.5px] text-ink">
          {car.nazev}
          <span className="ml-2 font-mono text-[11px] text-ink-3">
            {[car.spz && `SPZ ${car.spz}`, car.vin && `VIN ${car.vin}`, car.zdroj === 'nove' ? 'nové' : 'ojeté']
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </div>
      {car.vlastnik_firma && (
        <p className="font-mono text-[11px] text-ink-3" data-testid="terminy-vozu-vlastnik">
          Vůz patří firmě {car.vlastnik_firma} — termíny se zapíšou tam.
        </p>
      )}
      <Fields cols={kinds.length === 2 ? 2 : 1}>{kinds.map(field)}</Fields>
      {kinds.includes('servis') && data.servis_km && (
        <p className="font-mono text-[11px] text-ink-3" data-testid="terminy-vozu-km">
          {data.servis_km.akce === 'zapsat' && data.servis_km.dalsi_km != null
            ? `Půjčovna: příští servisní prohlídka při ${formatInt(data.servis_km.dalsi_km)} km (interval ${formatInt(data.servis_km.interval_km)} km, nájezd vozu ${formatInt(data.servis_km.nove_km)} km).`
            : data.servis_km.akce === 'beze_zmeny'
              ? `Půjčovna: nájezd poslední prohlídky (${formatInt(data.servis_km.posledni_km)} km) se nemění.`
              : 'Půjčovna: nájezd vozu není znám, kilometrový interval se nepřepočítá — zapište odečet ve vozovém parku.'}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Status strip in the event detail modal
// ============================================================================

interface PotvrzeniBlockProps {
  detail: ServisEventDetail;
  onConfirm: () => void;
  /** A pure detail: the state is shown, no action is offered. */
  readOnly?: boolean;
}

export function PotvrzeniBlock({ detail, onConfirm, readOnly = false }: PotvrzeniBlockProps) {
  const { can } = useModuleAccess();
  const canWrite = can('servis', 'write');
  const unconfirm = useUnconfirmServisEventMutation();

  // Vacations, shifts and deleted events have nothing to confirm.
  if (detail.smazano_at || (detail.typ_udalosti !== 0 && detail.typ_udalosti !== 2)) return null;

  const stav = detail.potvrzeni_stav ?? null;

  if (!stav && !detail.lze_potvrdit) {
    return (
      <p className="mb-5 text-[12px] text-ink-3" data-testid="potvrzeni-block">
        Provedení půjde potvrdit po začátku události.
      </p>
    );
  }

  const tone = stav === 'probehlo'
    ? 'border-ok bg-ok-soft text-ok'
    : stav === 'neprobehlo'
      ? 'border-red bg-red-soft text-red-text'
      : detail.k_potvrzeni
        ? 'border-warn bg-warn-soft text-warn'
        : 'border-line bg-surface-2 text-ink-2';
  const heading = stav === 'probehlo'
    ? 'Proběhlo'
    : stav === 'neprobehlo'
      ? 'Neproběhlo'
      : detail.k_potvrzeni
        ? 'Čeká na potvrzení provedení'
        : 'Provedení nepotvrzeno';
  const meta = stav ? formatPotvrzeno(detail) : 'Událost už začala. Potvrďte, jestli proběhla.';

  return (
    <div
      data-testid="potvrzeni-block"
      data-stav={stav ?? (detail.k_potvrzeni ? 'k_potvrzeni' : 'nepotvrzeno')}
      className={'mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 border px-4 py-3 ' + tone}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold">{heading}</div>
        {meta && <div className="text-[12px] opacity-90">{meta}</div>}
      </div>
      {!readOnly && (
      <div className="flex gap-2">
        {stav && canWrite && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={unconfirm.isPending}
            onClick={() =>
              unconfirm.mutate(detail.id_udalosti, {
                onSuccess: () => toast.ok('Potvrzení zrušeno'),
                onError: (err) => toast.error(errorMessage(err)),
              })
            }
          >
            Zrušit potvrzení
          </Button>
        )}
        <Button
          type="button"
          // Read-only users have no other action here → the blue primary;
          // with write access Uložit owns the bar, so this stays secondary.
          variant={!stav && !canWrite ? 'primary' : 'secondary'}
          size="sm"
          onClick={onConfirm}
          data-testid="potvrzeni-open"
        >
          {stav ? 'Změnit' : 'Potvrdit provedení'}
        </Button>
      </div>
      )}
    </div>
  );
}
